import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";
import Segmented from "@/components/enhance/Segmented";
import SourceList from "@/components/bgv/SourceList";
import { HOW, VOICES, createBgv, listBgvSources } from "@/lib/bgvApi";
import { safeApiError } from "@/lib/safeErrorText";

const TABS = [["songs", "Song in my library"], ["enhanced", "Enhanced vocal"], ["takes", "Recording Studio take"]];
const EMPTY = { enhanced: [], songs: [], takes: [], suno_ready: false };

function sourceBody(pick) {
  if (pick.kind === "take") return { kind: "take", session_id: pick.session_id, track_id: pick.track_id, take_id: pick.take_id };
  return { kind: pick.kind, id: pick.id, version: pick.version || "polished" };
}

/** Choose the lead, how the singers are made, and start. */
export default function StartCard({ initial }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState(initial.tab);
  const [pick, setPick] = useState(initial.pick);
  const [sources, setSources] = useState(null);
  const [q, setQ] = useState("");
  const [how, setHow] = useState("harmony");
  const [voices, setVoices] = useState("female");
  const [lyrics, setLyrics] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => listBgvSources(q).then(setSources).catch(() => setSources(EMPTY)), q ? 300 : 0);
    return () => clearTimeout(t);
  }, [q]);
  const choirOk = Boolean(pick?.has_music && sources?.suno_ready);
  useEffect(() => { if (!choirOk) setHow("harmony"); }, [choirOk]);
  const start = async () => {
    setBusy(true);
    try {
      const d = await createBgv({ source: sourceBody(pick), how, voices, ...(lyrics.trim() ? { lyrics } : {}) });
      navigate(`/create/background-singers/${d.id}`);
    } catch (e) { toast.error("Couldn't start", { description: safeApiError(e) }); setBusy(false); }
  };
  return (
    <section data-testid="bgv-start" className="space-y-6 rounded-3xl border border-violet-300/20 bg-[#0d0c18]/85 p-5 sm:p-7">
      <div>
        <h2 className="font-display text-base font-semibold md:text-lg">1 · Pick your lead vocal</h2>
        <div className="mt-3"><Segmented label="From" value={tab} options={TABS} onChange={setTab} testId="bgv-tab" /></div>
        <div className="mt-3"><SourceList tab={tab} sources={sources} pick={pick} onPick={setPick} q={q} onSearch={setQ} /></div>
      </div>
      <div className="space-y-4">
        <h2 className="font-display text-base font-semibold md:text-lg">2 · How should the singers be made?</h2>
        <Segmented label="Singers" value={how} options={HOW} onChange={setHow} testId="bgv-start-how" off={choirOk ? [] : ["choir", "both"]} />
        {!choirOk && pick ? <p data-testid="bgv-start-choir-note" className="text-[12px] text-white/45">{pick.has_music ? "The AI choir needs your ELPM music engine connection." : "The AI choir needs music to sing over — pick a library song or a take recorded over music."}</p> : null}
        {how !== "choir" ? <Segmented label="Voices" value={voices} options={VOICES} onChange={setVoices} testId="bgv-start-voices" /> : null}
        <label className="block">
          <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.16em] text-white/50">Lyrics with [Chorus] tags (optional)</span>
          <textarea data-testid="bgv-start-lyrics" value={lyrics} onChange={(e) => setLyrics(e.target.value)} rows={4} maxLength={6000}
            placeholder={"So the singers come in on the choruses. Songs from your library use their own lyrics.\n[Chorus]\nTrue love comes from within…"}
            className="w-full rounded-2xl border border-white/10 bg-black/40 px-4 py-3 text-[13px] text-white placeholder:text-white/30 focus:border-violet-300/50 focus:outline-none" />
        </label>
      </div>
      <button type="button" data-testid="bgv-start-button" onClick={start} disabled={!pick || busy}
        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-white py-3 text-[14px] font-semibold text-black transition-transform duration-200 hover:scale-[1.01] disabled:opacity-40 disabled:hover:scale-100">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Add background singers
      </button>
    </section>
  );
}
